import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import useIdleTimer from '../hooks/useIdleTimer';
import IdleWarningModal from './IdleWarningModal';
import { useAuth } from '../App';
import { queueLogoutNotice } from '../utils/logoutNotice';

// IDLE-EVENTS-001 — bounded PDF/iframe reading grace (see activity effect).
const PDF_READ_GRACE_MS = 10 * 60 * 1000;

// Default config (will be overridden by backend settings)
const DEFAULT_IDLE_CONFIG = {
  timeout: 15 * 60 * 1000, // 15 minutes default
  warningTime: 13 * 60 * 1000, // Warning 2 min before timeout
  enabled: true,
  activityPingInterval: 60 * 1000, // Update activity every 60 seconds on real interactions
  heartbeatInterval: 60 * 1000 // Check session status every 60 seconds
};

const IdleManager = ({ children }) => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [hasUnsavedForms, setHasUnsavedForms] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [idleConfig, setIdleConfig] = useState(DEFAULT_IDLE_CONFIG);
  const lastActivityPingRef = useRef(0);
  const activityPendingRef = useRef(false);
  const heartbeatIntervalRef = useRef(null);
  const isLoggingOutRef = useRef(false); // Prevent duplicate logout calls
  // Jun 2026 — refs so the activity-listener effect doesn't depend on
  // modal state (re-running it caused a backend activity ping the
  // moment the warning modal opened, silently extending the session).
  const showWarningModalRef = useRef(false);
  const initialPingDoneRef = useRef(false);
  // IDLE-EVENTS-001 (Jun 2026) — PDF/iframe bounded reading grace + a ref
  // handle to the frontend timer reset so both clocks stay synchronized.
  const iframeGraceUntilRef = useRef(0);
  const resetIdleTimerRef = useRef(null);
  // Feb 2026 P0 (IDLE-STAY-FALSE-FAILURE) — re-entrancy guard. The
  // warning modal's button used to fire `handleStayLoggedIn` twice
  // per click (onMouseDown + onClick). The duplicate listener is
  // gone (see IdleWarningModal.jsx), but we also defend in depth so
  // any future regression — or a fast double-click — can't spawn two
  // concurrent /api/session/activity calls and produce a phantom
  // "Couldn't reach the server" toast from the racing second call.
  const staySignedInInFlightRef = useRef(false);
  // Feb 7, 2026 — defensive grace window after a successful "Stay
  // Signed In". Background polls (dashboard stats, team activity,
  // billing, etc.) can race with the activity-ping commit in Redis
  // and return a labeled idle_timeout 401 milliseconds after the
  // user just rescued the session. Without this guard, the
  // `idle-timeout-401` window event re-opens the warning modal a
  // fraction of a second after the user dismissed it — and the
  // countdown then runs to 0 and forces logout. The grace window
  // suppresses idle_timeout 401 handling for `STAY_SIGNED_IN_GRACE_MS`
  // after the activity ping succeeds, by which point Redis has
  // committed the new last_activity value.
  //
  // Feb 26, 2026 (IDLE-STAY-NETSUSPEND) — Bumped 30 → 90 s. The old
  // 30 s window did NOT actually "cover one heartbeat" (heartbeat
  // interval is 60 s, so the grace expired BEFORE the next ping).
  // When the activity ping itself failed (e.g. Chromium
  // ERR_NETWORK_IO_SUSPENDED while the laptop briefly slept), the
  // backend never got the rescue write, the grace expired at 30 s,
  // and the t=60 s heartbeat re-opened the modal. 90 s covers the
  // next heartbeat plus the recovery-retry backoff schedule below.
  const staySignedInGraceUntilRef = useRef(0);
  const STAY_SIGNED_IN_GRACE_MS = 90 * 1000;
  const insideStaySignedInGrace = () => Date.now() < staySignedInGraceUntilRef.current;
  // Feb 26, 2026 — background recovery state. When the rescue ping
  // fails on a flaky/suspended network, we keep retrying in the
  // background (backoff + `online` listener) so the backend
  // last_activity catches up without the user needing to click Stay
  // Signed In a second time.
  const recoveryTimeoutRef = useRef(null);
  const recoveryOnlineHandlerRef = useRef(null);

  useEffect(() => {
    showWarningModalRef.current = showWarningModal;
    // IDLE-TOAST-SUPPRESS-001: expose modal visibility so the global
    // toast.error wrapper (App.js) can mute generic API-error toasts while
    // the "Are you still there?" warning is on screen. When the modal
    // closes, also clear the idle-401 stamp so real errors surface again.
    try {
      window.__TF_IDLE_MODAL_OPEN = showWarningModal;
      if (!showWarningModal) window.__TF_IDLE401_LAST = 0;
    } catch (_) { /* non-browser */ }
  }, [showWarningModal]);

  // Handle idle timeout logout - SINGLE SOURCE OF TRUTH
  const handleIdleLogout = useCallback(async (reason = 'inactivity') => {
    // Prevent duplicate logout calls
    if (isLoggingOutRef.current) {
      console.log('Logout already in progress, skipping duplicate call');
      return;
    }
    isLoggingOutRef.current = true;
    
    console.log(`Idle logout triggered: ${reason}`);
    
    // Dismiss all existing toasts first to prevent duplicates
    toast.dismiss();
    
    // Feb 2026 unified logout notice — replaces the old
    // `localStorage.logoutReason` inline banner. The Login page
    // will render a single glassmorphism toast via
    // showGlassmorphismLogoutNotice on mount.
    // Jun 2026 — scenario-aware: a labeled non-idle reason (e.g.
    // token_expired) gets its own copy instead of "inactivity".
    queueLogoutNotice(
      ['token_expired', 'password_changed', 'role_changed', 'token_revoked', 'session_invalid'].includes(reason)
        ? reason
        : 'idle_timeout'
    );
    
    // SECURITY FIX: Clear any cached navigation state
    // This ensures user always goes to dashboard after re-login
    localStorage.removeItem('lastRoute');
    localStorage.removeItem('redirectAfterLogin');
    sessionStorage.removeItem('lastRoute');
    sessionStorage.removeItem('redirectAfterLogin');
    
    // Clear any modals
    setShowWarningModal(false);
    
    // Perform logout — AuthContext.logout() already calls
    // window.location.replace('/login'), so the navigate() call
    // below is intentionally removed (it was a no-op anyway
    // since the page is mid-reload).
    if (logout) {
      // RECON-002 — tell the backend WHY, so the attendance session is stamped idle_timeout / token_expired, not "manual"
      const backendReason = ['inactivity', 'timeout_exceeded', 'backend_idle_timeout', 'idle_timeout'].includes(reason)
        ? 'idle_timeout' : reason === 'token_expired' ? 'token_expired' : 'manual';
      await logout({ reason: backendReason });
    } else {
      localStorage.removeItem('token');
      window.location.replace('/login');
    }

    // Reset logout flag after a short delay
    setTimeout(() => {
      isLoggingOutRef.current = false;
    }, 2000);
  }, [logout]);

  // Fetch idle timeout config from backend
  const fetchIdleConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/idle-timeout`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIdleConfig(prev => {
          const newTimeout = data.timeout_seconds * 1000;
          const newWarning = data.warning_seconds * 1000;
          
          // Only update if values changed
          if (prev.timeout !== newTimeout || prev.warningTime !== newWarning) {
            console.debug(`Idle config updated: ${data.timeout_minutes} min timeout, warning at ${data.timeout_minutes - 2} min`);
            return {
              ...prev,
              timeout: newTimeout,
              warningTime: newWarning
            };
          }
          return prev;
        });
      } else if (response.status === 401) {
        // Jun 2026 — label-aware handling. Previously ANY 401 here
        // (config poll every 2 min) fired an instant logout with no
        // warning modal. Now: idle-labeled → warning modal; other
        // labeled reasons → real logout; UNLABELED (transient) → ignore.
        const reason = response.headers.get('X-Logout-Reason');
        let detail = '';
        try {
          detail = (await response.json())?.detail || '';
        } catch (_) { /* no body */ }
        if (reason === 'idle_timeout' || detail.toLowerCase().includes('inactivity')) {
          window.dispatchEvent(new CustomEvent('idle-timeout-401', {
            detail: { source: 'IdleManager idle-config poll', url: '/api/settings/idle-timeout' }
          }));
        } else if (reason) {
          handleIdleLogout(reason);
        } else {
          console.warn('[IdleManager] Unlabeled 401 on idle-config poll ignored (transient).');
        }
      }
    } catch (error) {
      console.debug('Error fetching idle config:', error.message);
    }
  }, [handleIdleLogout]);

  // Heartbeat: Check session status with backend (does NOT reset idle timer)
  const checkSessionStatus = useCallback(async () => {
    if (!user) return;
    
    // DIAGNOSTIC: Log session ping
    console.debug('[IdleManager] Checking session status via /api/session/ping...');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.debug('[IdleManager] No token found, triggering logout');
        handleIdleLogout('no_token');
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/session/ping`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.debug('[IdleManager] Session ping response:', response.status);
      
      if (response.status === 401) {
        // AUTH-RESILIENCE (Feb 2026) — only treat 401 as logout when
        // labeled. Unlabeled 401s on /session/ping used to fire
        // `handleIdleLogout('session_invalid')` and kick the user
        // out for transient Redis/Mongo blips. Now the backend
        // returns 503 for infra issues; an unlabeled 401 should
        // only happen for legitimate (rare) "token has no sub"
        // edge cases. Defer to the global axios response
        // interceptor (App.js) — it knows whether to redirect.
        const logoutReason = response.headers.get('X-Logout-Reason');
        console.debug('[IdleManager] 401 received, logout reason:', logoutReason);
        // Jun 2026 — also detect idle expiry from the response body so
        // a proxy stripping custom headers can't silence the warning.
        let pingDetail = '';
        try {
          pingDetail = (await response.json())?.detail || '';
        } catch (_) { /* no body */ }
        const isIdleExpiry = logoutReason === 'idle_timeout' ||
          pingDetail.toLowerCase().includes('inactivity');
        if (isIdleExpiry) {
          console.log('[IdleManager] Backend enforced idle timeout');
          // Feb 7, 2026 — F1 (defensive). Inside the post-"Stay Signed
          // In" grace window, treat the heartbeat's idle 401 as a
          // stale race (Redis may not have committed the activity
          // write yet). Skip this tick silently; the next heartbeat
          // 60 s later will re-evaluate against fresh state.
          if (insideStaySignedInGrace()) {
            console.warn(
              '[IdleManager] Heartbeat idle_timeout 401 ignored within ' +
              'Stay-Signed-In grace window — Redis may not have committed.'
            );
            return;
          }
          // Show warning first if not already showing, instead of immediate logout
          if (!showWarningModal) {
            setShowWarningModal(true);
            const warningCountdown = Math.ceil((idleConfig.timeout - idleConfig.warningTime) / 1000);
            setCountdownSeconds(warningCountdown > 0 ? warningCountdown : 120);
          } else {
            // Warning already showing and backend says expired → logout
            handleIdleLogout('backend_idle_timeout');
          }
        } else {
          // Unlabeled 401 — DO NOT log out from here. The global
          // axios interceptor will trigger a real logout iff a
          // labelled 401 comes back on the user's next action.
          console.warn('[IdleManager] Unlabeled 401 ignored (no idle_timeout reason).');
        }
        return;
      }

      // AUTH-RESILIENCE (Feb 2026) — 503 = backend infra blip.
      // Skip this tick silently; we'll retry on the next interval.
      if (response.status === 503) {
        console.warn('[IdleManager] 503 from /session/ping — retrying next tick.');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        const remainingMs = data.remaining_seconds * 1000;
        const warningThreshold = 2 * 60 * 1000; // 2 minutes
        
        // Feb 2026 P0 (IDLE-STAY-FALSE-FAILURE) — inside the
        // Stay-Signed-In grace window the backend's
        // `remaining_seconds` may still reflect the PRE-click state
        // (the activity-ping commit in Redis hasn't yet rolled into
        // the ping endpoint's read). If we honour that stale value
        // we re-open the warning modal a heartbeat after the user
        // dismissed it — which is exactly what the user reported
        // (modal stays visible alongside the "Couldn't reach the
        // server" toast). Skip the modal-reopen branch entirely
        // during grace; the NEXT heartbeat (60 s) will re-evaluate
        // against fresh state.
        if (insideStaySignedInGrace()) {
          console.debug(
            '[IdleManager] Heartbeat 200 inside Stay-Signed-In grace — ' +
            `skipping modal-reopen (remainingMs=${remainingMs}, ` +
            `grace_left=${Math.max(0, staySignedInGraceUntilRef.current - Date.now())}ms).`
          );
          return;
        }
        
        // Show warning if approaching timeout
        if (remainingMs <= warningThreshold && remainingMs > 0 && !showWarningModal) {
          setShowWarningModal(true);
          setCountdownSeconds(Math.ceil(remainingMs / 1000));
        }
        
        // Update countdown if warning is showing — Jun 2026: only sync
        // DOWNWARD. Any stray backend clock reset must not bounce the
        // modal countdown back up (it previously jumped 2:00 → ~9:00
        // and the modal hung forever).
        if (showWarningModal && remainingMs > 0) {
          setCountdownSeconds(prev =>
            prev > 0 ? Math.min(prev, Math.ceil(remainingMs / 1000)) : Math.ceil(remainingMs / 1000)
          );
        }
        
        // Force logout if time is up
        if (remainingMs <= 0) {
          handleIdleLogout('timeout_exceeded');
        }
      }
    } catch (error) {
      console.debug('Session ping failed:', error.message);
    }
  }, [user, showWarningModal, handleIdleLogout]);

  // Ping backend to update activity timestamp (called on real user interactions)
  const pingBackendActivity = useCallback(async () => {
    if (!user) return;
    
    const now = Date.now();
    // Throttle: only ping if 60+ seconds since last ping
    if (now - lastActivityPingRef.current < idleConfig.activityPingInterval) {
      activityPendingRef.current = true;
      return;
    }
    
    lastActivityPingRef.current = now;
    activityPendingRef.current = false;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/session/activity`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 401) {
        // Jun 2026 — label-aware: idle-labeled → defer to the warning
        // modal (the user may still rescue the session within grace);
        // other labeled reasons → real logout; unlabeled → transient,
        // ignore (never log out from a background ping).
        const reason = response.headers.get('X-Logout-Reason');
        let detail = '';
        try {
          detail = (await response.json())?.detail || '';
        } catch (_) { /* no body */ }
        if (reason === 'idle_timeout' || detail.toLowerCase().includes('inactivity')) {
          window.dispatchEvent(new CustomEvent('idle-timeout-401', {
            detail: { source: 'IdleManager activity ping', url: '/api/session/activity' }
          }));
        } else if (reason) {
          handleIdleLogout(reason);
        }
      } else if (response.status >= 500) {
        // IDLE-RCA-002 — transient backend/infra failure: keep the activity
        // pending so the 60s flush retries instead of dropping it.
        activityPendingRef.current = true;
      }
    } catch (error) {
      console.debug('Activity ping failed:', error.message);
      activityPendingRef.current = true;
    }
  }, [user, idleConfig.activityPingInterval, handleIdleLogout]);

  // Fetch idle config on mount and periodically
  useEffect(() => {
    if (!user) return;
    
    // Small delay to ensure token is fully saved after login
    // This prevents race condition where IdleManager runs before token is available
    const initialFetchTimeout = setTimeout(() => {
      fetchIdleConfig();
    }, 500);
    
    // Re-fetch every 2 minutes to pick up admin changes faster
    const configInterval = setInterval(fetchIdleConfig, 2 * 60 * 1000);
    
    return () => {
      clearTimeout(initialFetchTimeout);
      clearInterval(configInterval);
    };
  }, [user, fetchIdleConfig]);

  // Setup heartbeat to check session status
  useEffect(() => {
    if (!user) return;
    
    // Guard: Clear any existing interval before creating new one (prevents double-mount issues)
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    
    // Small delay for initial check to ensure token is fully saved after login
    const initialCheckTimeout = setTimeout(() => {
      checkSessionStatus();
    }, 1000);
    
    // Heartbeat every 60 seconds to check if session is still valid
    heartbeatIntervalRef.current = setInterval(checkSessionStatus, idleConfig.heartbeatInterval);
    
    return () => {
      clearTimeout(initialCheckTimeout);
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [user, checkSessionStatus, idleConfig.heartbeatInterval]);

  // TEST: Keyboard shortcut to trigger idle modal (Ctrl+Shift+T)
  useEffect(() => {
    if (!user) return;
    
    const handleTestShortcut = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'T') {
        e.preventDefault();
        console.log('=== TEST: Triggering idle warning modal ===');
        setShowWarningModal(true);
        setCountdownSeconds(120);
      }
    };
    
    document.addEventListener('keydown', handleTestShortcut);
    return () => document.removeEventListener('keydown', handleTestShortcut);
  }, [user]);

  // Listen for idle-timeout-401 events dispatched by adminApi interceptor
  // This ensures the warning modal shows instead of an abrupt redirect
  useEffect(() => {
    if (!user) return;
    
    const handleApiIdleTimeout = (e) => {
      // Aug 2026 — the event now carries its real dispatcher + endpoint.
      // (Previous hardcoded "from adminApi" label misattributed 401s
      // raised by axios polls / IdleManager's own pings.)
      const src = e?.detail?.source || 'unknown source';
      const url = e?.detail?.url || 'unknown endpoint';
      console.log(`[IdleManager] Received idle-timeout-401 from ${src} (${url})`);
      // Feb 7, 2026 — F1 (defensive). If the user just rescued the
      // session via "Stay Signed In", ignore in-flight idle_timeout
      // 401s from concurrent background polls for the grace window.
      // The activity ping was already committed; this 401 is a stale
      // racer that would otherwise re-open the warning modal and
      // force a logout the user just dismissed.
      if (insideStaySignedInGrace()) {
        console.warn(
          '[IdleManager] Ignoring idle-timeout-401 within Stay-Signed-In ' +
          `grace window (${Math.max(0, staySignedInGraceUntilRef.current - Date.now())}ms left). ` +
          'The activity ping succeeded — the racing 401 is stale.'
        );
        return;
      }
      if (!showWarningModal && !isLoggingOutRef.current) {
        setShowWarningModal(true);
        const warningCountdown = Math.ceil((idleConfig.timeout - idleConfig.warningTime) / 1000);
        setCountdownSeconds(warningCountdown > 0 ? warningCountdown : 120);
      }
    };
    
    window.addEventListener('idle-timeout-401', handleApiIdleTimeout);
    return () => window.removeEventListener('idle-timeout-401', handleApiIdleTimeout);
  }, [user, showWarningModal, idleConfig.timeout, idleConfig.warningTime]);

  // Setup activity listeners for backend pings (real user interactions only)
  useEffect(() => {
    if (!user || !idleConfig.enabled) return;
    
    // IDLE-EVENTS-001 (Jun 2026) — root-cause fix for false idle warnings:
    // the backend clock previously missed inner-container scrolls (scroll
    // doesn't bubble → capture:true now), wheel/trackpad scrolling with a
    // stationary cursor ('wheel' added), and Tab/arrow/Backspace keys
    // (deprecated 'keypress' → 'keydown'). 'click' added for parity with
    // the frontend timer. pingBackendActivity throttles to 1 call/60s.
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'wheel', 'touchstart', 'mousemove', 'click'];
    
    const handleUserActivity = (e) => {
      // IDLE-LOGOUT-BTN-001 (Jun 2026) — events originating INSIDE the
      // warning modal must NOT be treated as "dismiss + extend" activity.
      // Dismissing here unmounts the modal between mousedown and mouseup,
      // so the buttons' click events never fire (Log Out silently acted
      // as Stay Signed In). The modal's own buttons decide the outcome.
      if (
        showWarningModalRef.current &&
        e && e.target && typeof e.target.closest === 'function' &&
        e.target.closest('[data-idle-modal]')
      ) {
        return;
      }
      // While the warning modal is open, passive mouse movement must
      // NOT dismiss it — only a deliberate action (click/key/scroll).
      if (e && e.type === 'mousemove' && showWarningModalRef.current) {
        return;
      }
      // Reset warning modal on any deliberate activity
      if (showWarningModalRef.current) {
        setShowWarningModal(false);
      }
      pingBackendActivity();
    };
    
    // Add listeners for real user activity. capture:true is REQUIRED so
    // scroll events from inner scrollable containers (fulfillment panels,
    // modals, tables) reach this listener — scroll does not bubble.
    activityEvents.forEach(event => {
      document.addEventListener(event, handleUserActivity, { passive: true, capture: true });
    });

    // IDLE-EVENTS-001 — PDF / iframe engagement (bounded reading grace).
    // Browser PDF plugins swallow events, so interactions inside an
    // <iframe>/<embed>/<object> never reach this document. Focusing INTO
    // the viewer (window blur while the tab stays visible and the active
    // element is an embed) counts as immediate activity and opens a
    // bounded grace window with throttled pings. The grace is NOT
    // renewed by itself — after it expires the user must interact again
    // (click/wheel/key/focus change). Hiding the tab cancels it instantly.
    const EMBED_TAGS = ['IFRAME', 'EMBED', 'OBJECT'];
    const markEmbedEngagement = () => {
      iframeGraceUntilRef.current = Date.now() + PDF_READ_GRACE_MS;
      pingBackendActivity();
      if (resetIdleTimerRef.current) resetIdleTimerRef.current();
    };
    const handleWindowBlur = () => {
      setTimeout(() => {
        const ae = document.activeElement;
        if (document.visibilityState === 'visible' && ae && EMBED_TAGS.includes(ae.tagName)) {
          markEmbedEngagement();
        }
      }, 0);
    };
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') {
        iframeGraceUntilRef.current = 0; // walk-away / tab switch: stop grace pings
      }
    };
    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('visibilitychange', handleVisibility);
    
    // Periodic check to flush pending activity
    const flushInterval = setInterval(() => {
      if (activityPendingRef.current) {
        pingBackendActivity();
      }
      // PDF reading grace: while the embed keeps focus, the tab is visible
      // and the bounded window hasn't expired, keep both clocks alive.
      // Never auto-extends past the grace and never dismisses the warning.
      const ae = document.activeElement;
      if (
        !showWarningModalRef.current &&
        document.visibilityState === 'visible' &&
        Date.now() < iframeGraceUntilRef.current &&
        ae && EMBED_TAGS.includes(ae.tagName)
      ) {
        pingBackendActivity();
        if (resetIdleTimerRef.current) resetIdleTimerRef.current();
      }
    }, idleConfig.activityPingInterval);
    
    // One-time ping on FIRST mount only. (Jun 2026 — this effect used
    // to depend on `showWarningModal`, so it re-ran and re-pinged the
    // moment the warning modal opened, resetting the backend idle
    // clock and corrupting the countdown.)
    if (!initialPingDoneRef.current) {
      initialPingDoneRef.current = true;
      pingBackendActivity();
    }
    
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleUserActivity, { capture: true });
      });
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(flushInterval);
    };
  }, [user, idleConfig.enabled, idleConfig.activityPingInterval, pingBackendActivity]);

  // Check for unsaved form data
  const checkForUnsavedForms = useCallback(() => {
    const formInputs = document.querySelectorAll('input, textarea, select');
    let hasUnsaved = false;
    formInputs.forEach(input => {
      if (input.value && input.value.trim() !== '' && !input.readOnly) {
        hasUnsaved = true;
      }
    });
    setHasUnsavedForms(hasUnsaved);
  }, []);

  // Handle idle from useIdleTimer hook — show warning, do NOT logout immediately
  const handleIdle = useCallback(() => {
    console.log('Frontend idle timer triggered — showing warning modal');
    // Feb 2026 P0 (IDLE-STAY-FALSE-FAILURE) — never re-open the modal
    // inside the Stay-Signed-In grace window. The frontend timer
    // should already have been reset by handleStayLoggedIn, but a
    // very-fast double-click or a clock skew on the useIdleTimer
    // internal scheduler could fire onIdle again before the reset
    // settles. Belt-and-suspenders.
    if (insideStaySignedInGrace()) {
      console.debug('[IdleManager] handleIdle suppressed within Stay-Signed-In grace window.');
      return;
    }
    checkForUnsavedForms();
    if (!showWarningModal) {
      setShowWarningModal(true);
      const remainingSeconds = Math.ceil((idleConfig.timeout - idleConfig.warningTime) / 1000);
      setCountdownSeconds(remainingSeconds > 0 ? remainingSeconds : 120);
    }
  }, [checkForUnsavedForms, idleConfig.timeout, idleConfig.warningTime, showWarningModal]);

  // Handle warning from useIdleTimer hook
  const handleWarning = useCallback(() => {
    console.log('Frontend warning triggered');
    // Feb 2026 P0 (IDLE-STAY-FALSE-FAILURE) — same suppression as
    // handleIdle above. Without this guard the useIdleTimer hook's
    // warning callback could re-show the modal milliseconds after the
    // user clicked Stay Signed In and resetIdleTimer() committed.
    if (insideStaySignedInGrace()) {
      console.debug('[IdleManager] handleWarning suppressed within Stay-Signed-In grace window.');
      return;
    }
    checkForUnsavedForms();
    setShowWarningModal(true);
    const remainingSeconds = Math.ceil((idleConfig.timeout - idleConfig.warningTime) / 1000);
    setCountdownSeconds(remainingSeconds);
  }, [checkForUnsavedForms, idleConfig.timeout, idleConfig.warningTime]);

  // Handle active from useIdleTimer hook
  const handleActive = useCallback(() => {
    setShowWarningModal(false);
  }, []);

  // Use the idle timer hook
  const {
    isIdle,
    remainingTime,
    warningTime,
    reset: resetIdleTimer,
    trackApiActivity
  } = useIdleTimer({
    timeout: idleConfig.timeout,
    warningTime: idleConfig.warningTime,
    onIdle: handleIdle,
    onWarning: handleWarning,
    onActive: handleActive,
    enabled: idleConfig.enabled && !!user
  });

  // IDLE-EVENTS-001 — expose the frontend timer reset to the activity
  // effect above (declared earlier), keeping both clocks synchronized.
  useEffect(() => {
    resetIdleTimerRef.current = resetIdleTimer;
  }, [resetIdleTimer]);

  // Feb 26, 2026 (IDLE-STAY-NETSUSPEND) — background recovery helper.
  // When the rescue activity-ping fails (network suspended, transient
  // 5xx) we keep retrying in the background with exponential backoff
  // AND also retry immediately when the browser fires `online`. The
  // Stay-Signed-In grace window is held open across the entire
  // recovery sequence so the heartbeat / 401 listeners cannot
  // re-open the warning modal while we're still trying to commit.
  //
  // Capped at 4 retries with backoff 5s, 10s, 20s, 30s (≈ 65 s total)
  // — plenty to outlast a typical Chromium ERR_NETWORK_IO_SUSPENDED
  // window. Stops on first 2xx; logs out the user only if a retry
  // surfaces an EXPLICIT forced-logout 401 reason.
  const scheduleStaySignedInRecovery = useCallback(() => {
    const backoffs = [5000, 10000, 20000, 30000];
    let attempt = 0;
    const cleanup = () => {
      if (recoveryTimeoutRef.current) {
        clearTimeout(recoveryTimeoutRef.current);
        recoveryTimeoutRef.current = null;
      }
      if (recoveryOnlineHandlerRef.current) {
        window.removeEventListener('online', recoveryOnlineHandlerRef.current);
        recoveryOnlineHandlerRef.current = null;
      }
    };
    // Reset any prior recovery cycle so duplicate Stay-Signed-In
    // clicks can't stack overlapping retry loops.
    cleanup();

    const attemptRecovery = async () => {
      const token = localStorage.getItem('token');
      if (!token) { cleanup(); return; }
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/session/activity`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (r.status >= 200 && r.status < 300) {
          lastActivityPingRef.current = Date.now();
          // Re-extend the grace window from the successful recovery
          // so any in-flight heartbeat 401 racing this commit is
          // still suppressed.
          staySignedInGraceUntilRef.current = Date.now() + STAY_SIGNED_IN_GRACE_MS;
          console.log(
            `[IdleManager:StaySignedIn] Background recovery succeeded ` +
            `on attempt ${attempt + 1}. Backend last_activity is now fresh.`
          );
          try {
            window.__TF_IDLE401_LAST = 0;
            window.dispatchEvent(new CustomEvent('session-restored', { detail: { source: 'background-recovery' } }));
          } catch (_) { /* non-browser */ }
          cleanup();
          return;
        }
        if (r.status === 401) {
          // Mirror handleStayLoggedIn's logic: only forced-logout
          // reasons should kick the user out from a recovery retry.
          const reason = r.headers.get('X-Logout-Reason');
          const isForcedLogoutReason = ['password_changed', 'role_changed', 'token_revoked', 'session_invalid']
            .includes(reason);
          if (isForcedLogoutReason) {
            console.warn(`[IdleManager:StaySignedIn] Recovery saw forced-logout "${reason}" — logging out.`);
            cleanup();
            handleIdleLogout(reason);
            return;
          }
          // Idle/unlabeled 401 from a recovery retry is treated the
          // same as a network failure: keep trying. The user clearly
          // chose Stay Signed In; the backend will catch up on the
          // next attempt.
        }
        console.warn(
          `[IdleManager:StaySignedIn] Recovery attempt ${attempt + 1} ` +
          `non-success status=${r.status}; will retry.`
        );
      } catch (err) {
        console.warn(
          `[IdleManager:StaySignedIn] Recovery attempt ${attempt + 1} ` +
          `network error: ${err?.message || err}`
        );
      }

      attempt += 1;
      if (attempt >= backoffs.length) {
        console.warn('[IdleManager:StaySignedIn] Recovery gave up after all retries — next user interaction will resync.');
        cleanup();
        return;
      }
      // Extend the grace window across the upcoming retry so the
      // heartbeat can't fire a stale-state modal in between.
      staySignedInGraceUntilRef.current = Math.max(
        staySignedInGraceUntilRef.current,
        Date.now() + backoffs[attempt] + 5000,
      );
      recoveryTimeoutRef.current = setTimeout(attemptRecovery, backoffs[attempt]);
    };

    // Hold the grace window open for the full recovery budget upfront
    // so heartbeats during the wait don't reopen the modal.
    const totalBudget = backoffs.reduce((a, b) => a + b, 0) + 5000;
    staySignedInGraceUntilRef.current = Math.max(
      staySignedInGraceUntilRef.current,
      Date.now() + totalBudget,
    );

    // Retry immediately when the browser reports the radio came back
    // (e.g. after Chromium ERR_NETWORK_IO_SUSPENDED clears). We bind
    // the listener BEFORE the first delayed retry so we don't wait
    // through the backoff if the network is already live.
    recoveryOnlineHandlerRef.current = () => {
      console.log('[IdleManager:StaySignedIn] `online` event — retrying immediately.');
      attemptRecovery();
    };
    window.addEventListener('online', recoveryOnlineHandlerRef.current);

    // First attempt after the smallest backoff (5 s) so the network
    // has a chance to recover before we burn an attempt.
    recoveryTimeoutRef.current = setTimeout(attemptRecovery, backoffs[0]);
  }, [handleIdleLogout, STAY_SIGNED_IN_GRACE_MS]);

  // Tear down any pending recovery on unmount or logout.
  useEffect(() => {
    return () => {
      if (recoveryTimeoutRef.current) clearTimeout(recoveryTimeoutRef.current);
      if (recoveryOnlineHandlerRef.current) {
        window.removeEventListener('online', recoveryOnlineHandlerRef.current);
      }
    };
  }, []);

  // Handle "Stay Logged In" button
  //
  // Feb 7, 2026 — Defensive rewrite (F2 + F3 of the Stay-Signed-In RCA).
  // ---------------------------------------------------------------
  // Previous version treated ANY 401 on /api/session/activity as
  // "session is dead, log out" — even when the 401 was a race with
  // Redis catching up (grace-window boundary) or a labeled non-idle
  // reason (token_revoked, password_changed). The new flow:
  //
  //   1. Optimistically close the modal + reset the frontend timer
  //      so the user gets immediate feedback.
  //   2. Open the STAY_SIGNED_IN grace window so background polls
  //      racing this commit cannot tear the session down (see F1
  //      handler above).
  //   3. POST /api/session/activity. On 200 → success toast.
  //   4. On 401 with `X-Logout-Reason: password_changed` /
  //      `role_changed` / `token_revoked` → those are NOT races,
  //      they're genuine forced-logout reasons. Log them out with
  //      the correct reason (no false-flagging as `idle_timeout`).
  //   5. On 401 with `X-Logout-Reason: idle_timeout` OR an
  //      unlabeled 401 → one-shot retry after 500 ms (gives Redis a
  //      chance to commit any prior write). If the retry also 401s
  //      → genuine session-expired, log out.
  //   6. On network error → optimistically stay signed in (frontend
  //      timer already reset; next activity ping in 60 s will recover).
  //
  // Logging trail uses the `[IdleManager:StaySignedIn]` prefix so the
  // bug-report evidence chain is unambiguous.
  const handleStayLoggedIn = useCallback(async () => {
    const startTs = Date.now();
    // Feb 2026 P0 (IDLE-STAY-FALSE-FAILURE) — re-entrancy guard. The
    // warning modal's button previously fired this callback twice
    // per click (onMouseDown + onClick). The duplicate listener is
    // gone, but a fast double-click could still kick off two
    // /api/session/activity races. The second race almost always
    // wins/loses against Redis differently and produces a misleading
    // "Couldn't reach the server" toast from one branch while the
    // other branch shows "Session extended successfully!". Bail out
    // immediately if a Stay-Signed-In flow is already in flight.
    if (staySignedInInFlightRef.current) {
      console.warn('[IdleManager:StaySignedIn] Already in flight — ignoring duplicate click.');
      return;
    }
    staySignedInInFlightRef.current = true;
    console.log('[IdleManager:StaySignedIn] User clicked — closing modal, resetting timers');
    setShowWarningModal(false);
    resetIdleTimer();

    // Open the grace window IMMEDIATELY (before the network call)
    // so any concurrent in-flight 401 that lands during the activity
    // ping's RTT is also suppressed.
    staySignedInGraceUntilRef.current = startTs + STAY_SIGNED_IN_GRACE_MS;
    console.log(
      `[IdleManager:StaySignedIn] Grace window opened until ` +
      `${new Date(staySignedInGraceUntilRef.current).toISOString()} (${STAY_SIGNED_IN_GRACE_MS}ms)`
    );

    const pingActivity = async (label) => {
      const token = localStorage.getItem('token');
      if (!token) return { status: 0, reason: null, detail: 'no_token' };
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/session/activity`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        let body = null;
        try { body = await r.clone().json(); } catch (_) { /* empty body */ }
        const out = {
          status: r.status,
          reason: r.headers.get('X-Logout-Reason') || null,
          detail: body?.detail || '',
          idle_seconds: body?.idle_seconds ?? null,
        };
        console.log(`[IdleManager:StaySignedIn] ${label} → status=${out.status}, reason=${out.reason || '(none)'}, idle_seconds=${out.idle_seconds}`);
        return out;
      } catch (err) {
        console.warn(`[IdleManager:StaySignedIn] ${label} network error: ${err?.message || err}`);
        return { status: -1, reason: null, detail: 'network_error' };
      }
    };

    let result;
    try {
      result = await pingActivity('initial activity ping');

      // F2: label-aware non-idle 401 — log out with the real reason
      if (result.status === 401) {
        const reason = result.reason;
        const isForcedLogoutReason = ['password_changed', 'role_changed', 'token_revoked', 'session_invalid']
          .includes(reason);
        if (isForcedLogoutReason) {
          console.warn(`[IdleManager:StaySignedIn] Forced logout reason "${reason}" — logging out with correct label.`);
          handleIdleLogout(reason);
          return;
        }
        // F2: one-shot retry for idle_timeout or unlabeled 401 — gives
        // Redis a chance to commit any in-flight last_activity write.
        console.warn('[IdleManager:StaySignedIn] First ping 401 (idle_timeout/unlabeled). Retrying once after 500ms…');
        await new Promise(r => setTimeout(r, 500));
        result = await pingActivity('retry activity ping');

        if (result.status === 401) {
          // Both attempts failed → genuine session-expired
          const finalReason = result.reason && result.reason !== 'idle_timeout'
            ? result.reason
            : 'idle_timeout';
          console.warn(`[IdleManager:StaySignedIn] Retry also 401 — session genuinely expired. Logging out as "${finalReason}".`);
          handleIdleLogout(finalReason);
          return;
        }
      }

      if (result.status >= 200 && result.status < 300) {
        lastActivityPingRef.current = Date.now();
        // Extend the grace window from the SUCCESSFUL ping so any
        // background 401 racing the same Redis write is still ignored
        // for the full window from this moment.
        staySignedInGraceUntilRef.current = Date.now() + STAY_SIGNED_IN_GRACE_MS;
        // Feb 26, 2026 — if a prior click's recovery loop is still
        // running (rare: user clicked Stay Signed In twice across a
        // network blip), tear it down — this fresh success makes it
        // redundant.
        if (recoveryTimeoutRef.current) {
          clearTimeout(recoveryTimeoutRef.current);
          recoveryTimeoutRef.current = null;
        }
        if (recoveryOnlineHandlerRef.current) {
          window.removeEventListener('online', recoveryOnlineHandlerRef.current);
          recoveryOnlineHandlerRef.current = null;
        }
        console.log(
          `[IdleManager:StaySignedIn] Activity ping succeeded after ` +
          `${Date.now() - startTs}ms. Backend idle_seconds=${result.idle_seconds}. ` +
          `Grace window extended to ${new Date(staySignedInGraceUntilRef.current).toISOString()}.`
        );
        toast.success('Session extended successfully!', {
          description: 'Your session has been refreshed.',
          duration: 3000
        });
        // IDLE-TOAST-SUPPRESS-001: session restored — clear the idle-401
        // marker and announce recovery so data views can refetch anything
        // that failed while the session was expiring (pages that poll will
        // recover on their next tick regardless).
        try {
          window.__TF_IDLE401_LAST = 0;
          window.dispatchEvent(new CustomEvent('session-restored', { detail: { source: 'stay-signed-in' } }));
        } catch (_) { /* non-browser */ }
        return;
      }

      // Network error or 5xx — don't log out (frontend timer is already
      // reset). Feb 26, 2026 (IDLE-STAY-NETSUSPEND) — start a
      // background recovery loop that keeps retrying the activity
      // ping until either it succeeds or we give up. Retries bypass
      // the 60 s throttle of pingBackendActivity and ALSO trigger on
      // the next `online` event, so a Chromium ERR_NETWORK_IO_SUSPENDED
      // suspension that lifts mid-retry recovers immediately. While
      // recovery is pending the Stay-Signed-In grace window is held
      // open so the heartbeat cannot re-show the warning modal.
      console.warn(
        `[IdleManager:StaySignedIn] Non-success status ${result.status} — ` +
        'starting background recovery (frontend timer already reset).'
      );
      const wasNetworkErr = result.status === -1;
      toast.warning(
        wasNetworkErr
          ? 'Couldn\'t reach the server, but you\'re still signed in.'
          : `Server returned ${result.status} while extending the session — you\'re still signed in.`,
        {
          description: 'Reconnecting in the background — no action needed.',
          duration: 5000,
        }
      );
      scheduleStaySignedInRecovery();
    } finally {
      staySignedInInFlightRef.current = false;
    }
  }, [resetIdleTimer, handleIdleLogout, scheduleStaySignedInRecovery, STAY_SIGNED_IN_GRACE_MS]);

  // Handle logout button - uses same robust logic as handleIdleLogout
  const handleLogout = useCallback(async () => {
    // Prevent duplicate logout calls
    if (isLoggingOutRef.current) {
      console.log('Logout already in progress, skipping duplicate call');
      return;
    }
    isLoggingOutRef.current = true;
    
    console.log('Manual logout triggered from idle modal');
    console.log('logout function available:', !!logout);
    console.log('navigate function available:', !!navigate);
    
    // Dismiss all existing toasts first to prevent duplicates
    toast.dismiss();
    
    // Clear any modals FIRST
    setShowWarningModal(false);
    
    // Feb 2026 unified logout notice — manual logout from idle modal.
    queueLogoutNotice('manual');
    
    // Perform logout via AuthContext
    try {
      if (logout && typeof logout === 'function') {
        console.log('Calling logout function from AuthContext');
        await logout();
        console.log('Logout completed successfully');
      } else {
        // Fallback: clear token manually if logout function not available
        console.log('Logout function not available, clearing tokens manually');
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local storage on error
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
    
    // Navigate to login - ALWAYS navigate regardless of logout result
    // P0 SECURITY: Use window.location.replace to prevent back button from returning
    console.log('Redirecting to /login with hard redirect');
    window.location.replace('/login');
  }, [logout, navigate]);

  // Countdown timer for warning modal
  useEffect(() => {
    if (!showWarningModal) return;
    
    const countdownInterval = setInterval(() => {
      setCountdownSeconds(prev => {
        if (prev <= 1) {
          handleIdleLogout('countdown_expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(countdownInterval);
  }, [showWarningModal, handleIdleLogout]);

  return (
    <>
      {children}
      {showWarningModal && (
        <IdleWarningModal
          isOpen={showWarningModal}
          countdown={countdownSeconds}
          hasUnsavedWork={hasUnsavedForms}
          idleMinutes={Math.max(1, Math.round(idleConfig.warningTime / 60000))}
          onStayLoggedIn={handleStayLoggedIn}
          onLogout={handleLogout}
        />
      )}
    </>
  );
};

export default IdleManager;
